import { type CountryCode } from '@constants'
import { useDebounceFnV2 } from '@dni-hooks/useDebounceFnV2'
import { useApiECommerceValidateInvoice } from '@hooks-api/eCommerceV2/use-api-e-commerce-validate-invoice'
import { apiStatusToCarrierStatus, CarrierValidationStatus, type CarrierValidationStatusType, deriveLocalCarrierStatus, PHONE_CARRIER_VALIDATE_TYPE } from '@wp-checkout/components/InvoiceTypes/carrier-status'
import { stripPrefix } from '@wp-checkout/components/InvoiceTypes/PhoneCarrierField/phone-carrier-value'
import { useEffect, useRef, useState } from 'react'

const VALIDATE_DEBOUNCE_MS = 500

export const useMerchantCarrierFieldValidation = (countryCode: CountryCode, phoneCarrierNumber: string): CarrierValidationStatusType => {
  const { trigger } = useApiECommerceValidateInvoice()
  const [status, setStatus] = useState<CarrierValidationStatusType>(CarrierValidationStatus.EMPTY)

  const latestValueRef = useRef(phoneCarrierNumber)
  latestValueRef.current = phoneCarrierNumber

  const validateCarrierExistence = useDebounceFnV2(async (value: string) => {
    try {
      const response = await trigger({ country_code: countryCode, type: PHONE_CARRIER_VALIDATE_TYPE, value })
      if (latestValueRef.current !== value) return
      setStatus(apiStatusToCarrierStatus[response.data.data.status])
    } catch {
      if (latestValueRef.current !== value) return
      setStatus(CarrierValidationStatus.SYSTEM_ERROR)
    }
  }, VALIDATE_DEBOUNCE_MS)

  useEffect(function syncMerchantCarrierValidationStatus() {
    const localStatus = deriveLocalCarrierStatus(stripPrefix(phoneCarrierNumber))
    setStatus(localStatus)
    if (localStatus === CarrierValidationStatus.VALIDATING) {
      validateCarrierExistence(phoneCarrierNumber)
    } else {
      validateCarrierExistence.clear()
    }
  }, [phoneCarrierNumber, validateCarrierExistence])

  return status
}
